import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Box, Button, TextField, Grid, Card, CardContent, Typography, Select, MenuItem, InputLabel, FormControl, CircularProgress } from "@mui/material";
import { IoMdArrowRoundBack } from "react-icons/io";
import ApiClient from "../../api-client";
import { generateTempPassword } from "../../utils/common";

function AddCustomer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [profilePicPrev, setProfilePicPrev] = useState();
  const [loader, setLoader] = useState(false);
  const [inputName, setInputName] = useState("");
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    gender: "",
    dateOfBirth: "",
    address1: "",
    address2: "",
    city: "",
    pincode: "",
    profilePic: "",
  });

  const validate = () => {
    const errors = {};
    if (!formData.firstName) errors.firstName = "First name is required";
    if (!formData.lastName) errors.lastName = "Last name is required";
    if (formData.phoneNumber && formData.phoneNumber.toString().length !== 9)
      errors.phoneNumber = "Phone number is invalid";
    if (!formData.email) errors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = "Email address is invalid";
    return errors;
  };

  useEffect(() => {
    if (inputName) {
      const validationErrors = validate();
      setErrors((previousData) => ({
        ...previousData,
        [inputName]: validationErrors[inputName] || "",
      }));
    }
  }, [inputName, formData]);

  const handleImageChange = (e) => {
    setFormData((prev) => ({ ...prev, profilePic: e.target.files[0] }));
    setProfilePicPrev(URL.createObjectURL(e.target.files[0]));
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setInputName(name);
  };

  const resetForm = () => {
    setProfilePicPrev("");
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phoneNumber: "",
      gender: "",
      dateOfBirth: "",
      address1: "",
      address2: "",
      city: "",
      pincode: "",
      profilePic: "",
    });
  };

  const handleSubmit = async () => {
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      setErrors({});
      setLoader(true);
      try {
        let registerFormData = new FormData();
        let updateFormData = new FormData();
        const tempPass = generateTempPassword();
        for (const key in formData) {
          if (key === "firstName" || key === "lastName" || key === "email") {
            registerFormData.append(key, formData[key]);
          } else {
            updateFormData.append(key, formData[key]);
          }
        }
        registerFormData.append("isAdminCreated", "admin");
        registerFormData.append("role", "customer");
        registerFormData.append("password", tempPass);
        registerFormData.append("confirmPassword", tempPass);

        const registerCustomer = await ApiClient._postFormData(
          `api/users/signup/customer`,
          registerFormData,
          true,
          dispatch
        );
        if (registerCustomer.status) {
          updateFormData.append("id", registerCustomer?.data?._id);
          const updateCustomer = await ApiClient._putFormData(
            `api/users/updateprofile`,
            updateFormData,
            true,
            dispatch
          );
          if (updateCustomer.status) {
            resetForm();
            setLoader(false);
            navigate("/admin/customer/list");
          }
          toast.success(registerCustomer.message, { autoClose: 3000 });
        }
      } catch (error) {
        toast.error(error.message, { autoClose: 3000 });
      }
    }
  };

  return (
    <Box p={3}>
      <Button
        variant="contained"
        color="primary"
        startIcon={<IoMdArrowRoundBack />}
        onClick={() => navigate("/admin/customer/list")}
        sx={{ mb: 2 }}
      >
        Back
      </Button>

      <Card>
        <CardContent>
          <Typography variant="h5" mb={2}>Add Customer</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Phone Number"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleChange}
                error={!!errors.phoneNumber}
                helperText={errors.phoneNumber}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Gender</InputLabel>
                <Select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  label="Gender"
                >
                  <MenuItem value="">Select Gender</MenuItem>
                  <MenuItem value="male">Male</MenuItem>
                  <MenuItem value="female">Female</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Date of Birth"
                name="dateOfBirth"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={formData.dateOfBirth}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address 1"
                name="address1"
                value={formData.address1}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Address 2"
                name="address2"
                value={formData.address2}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="City"
                name="city"
                value={formData.city}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Pincode"
                name="pincode"
                value={formData.pincode}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <InputLabel>Profile Picture</InputLabel>
              <input type="file" accept=".jpeg, .jpg, .png" onChange={handleImageChange} />
              {profilePicPrev && <img src={profilePicPrev} alt="Profile" style={{ marginTop: "10px", width: "100px", height: "100px" }} />}
            </Grid>
          </Grid>
          <Box mt={2}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loader}
            >
              Add Customer {loader && <CircularProgress size={20} sx={{ ml: 1 }} />}
            </Button>
            <Button variant="outlined" color="secondary" sx={{ ml: 2 }} onClick={resetForm}>
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default AddCustomer;
